import { useState, useRef, useEffect } from 'react';
import { useMutation, gql } from '@/apollo';
import type { Location, LocationFormState } from '../types/moderator.types';
import { initialLocationState } from '../types/moderator.types';
import { logError } from '../../../utils/errorMessages';

// GraphQL Mutations
const CREATE_LOCATION_WITH_NOTES = gql`
  mutation CreateLocationWithNotes($input: CreateLocationWithNotesInput!) {
    createLocationWithNotes(input: $input) {
      id
      name
      city
      stateId
      state {
        id
        code
        name
      }
      customerId
      customer {
        id
        name
      }
      zipCode
      physicalAddress
      notes {
        id
        content
        order
      }
    }
  }
`;

const UPDATE_LOCATION_WITH_NOTES = gql`
  mutation UpdateLocationWithNotes($id: ID!, $input: UpdateLocationWithNotesInput!) {
    updateLocationWithNotes(id: $id, input: $input) {
      id
      name
      city
      stateId
      state {
        id
        code
        name
      }
      customerId
      customer {
        id
        name
      }
      zipCode
      physicalAddress
      notes {
        id
        content
        order
      }
    }
  }
`;

const DELETE_LOCATION_WITH_NOTES = gql`
  mutation DeleteLocationWithNotes($id: ID!) {
    deleteLocationWithNotes(id: $id)
  }
`;

export interface UseLocationManagementReturn {
  // State
  showForm: boolean;
  editing: Location | null;
  form: LocationFormState;
  searchTerm: string;
  newNote: string;
  stateSearchTerm: string;
  showStateDropdown: boolean;
  stateDropdownRef: React.RefObject<HTMLDivElement>;
  showDeleteModal: boolean;
  deleteId: string | null;
  
  // State setters
  setShowForm: (show: boolean) => void;
  setEditing: (location: Location | null) => void;
  setForm: (form: LocationFormState) => void;
  setSearchTerm: (term: string) => void;
  setNewNote: (note: string) => void;
  setStateSearchTerm: (term: string) => void;
  setShowStateDropdown: (show: boolean) => void;
  
  // Handlers
  handleCreate: (customerId?: string) => void;
  handleEdit: (location: Location) => void;
  handleDelete: (id: string) => void;
  handleConfirmDelete: () => void;
  handleCancelDelete: () => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleCancel: () => void;
  handleAddNote: () => void;
  handleRemoveNote: (index: number) => void;
  handleReorderNotes: (notes: string[]) => void;
  
  // Loading states
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const useLocationManagement = (
  _refetchLocations: () => void,
  refetchModeratorData: () => void
): UseLocationManagementReturn => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Location | null>(null);
  const [form, setForm] = useState<LocationFormState>(initialLocationState);
  const [searchTerm, setSearchTerm] = useState('');
  const [newNote, setNewNote] = useState('');
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const stateDropdownRef = useRef<HTMLDivElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Handle clicks outside state dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target as Node)) {
        setShowStateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [createLocationWithNotes, { loading: isCreating }] = useMutation(CREATE_LOCATION_WITH_NOTES, {
    onCompleted: () => {
      setShowForm(false);
      setEditing(null);
      setForm(initialLocationState);
      setNewNote('');
      setStateSearchTerm('');
      setShowStateDropdown(false);
      refetchModeratorData();
    },
    onError: (error) => {
      logError(error, 'Create Location');
    }
  });

  const [updateLocationWithNotes, { loading: isUpdating }] = useMutation(UPDATE_LOCATION_WITH_NOTES, {
    onCompleted: () => {
      setShowForm(false);
      setEditing(null);
      setForm(initialLocationState);
      setNewNote('');
      setStateSearchTerm('');
      setShowStateDropdown(false);
      refetchModeratorData();
    },
    onError: (error) => {
      logError(error, 'Update Location');
    }
  });

  const [deleteLocationWithNotes, { loading: isDeleting }] = useMutation(DELETE_LOCATION_WITH_NOTES, {
    onCompleted: () => {
      setShowDeleteModal(false);
      setDeleteId(null);
      refetchModeratorData();
    },
    onError: (error) => {
      logError(error, 'Delete Location');
    }
  });

  const handleCreate = (customerId?: string) => {
    setShowForm(true);
    setEditing(null);
    setForm({
      ...initialLocationState,
      customerId: customerId || ''
    });
  };

  const handleEdit = (location: Location) => {
    setShowForm(true);
    setEditing(location);
    setForm({
      name: location.name,
      city: location.city || '',
      stateId: location.stateId,
      customerId: location.customerId || '',
      zipCode: location.zipCode || '',
      physicalAddress: location.physicalAddress || '',
      notes: location.notes?.map(n => n.content) || []
    });
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      try {
        await deleteLocationWithNotes({ variables: { id: deleteId } });
        // Modal will be closed in onCompleted callback
      } catch (error) {
        // Error is already logged in onError callback
        // Don't close modal on error - let user retry or cancel
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editing) {
      await updateLocationWithNotes({
        variables: {
          id: editing.id,
          input: {
            name: form.name,
            city: form.city,
            stateId: form.stateId,
            customerId: form.customerId || null,
            zipCode: form.zipCode,
            physicalAddress: form.physicalAddress,
            notes: form.notes
          }
        }
      });
    } else {
      await createLocationWithNotes({
        variables: {
          input: {
            name: form.name,
            city: form.city,
            stateId: form.stateId,
            customerId: form.customerId || null,
            zipCode: form.zipCode,
            physicalAddress: form.physicalAddress,
            notes: form.notes
          }
        }
      });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm(initialLocationState);
    setNewNote('');
    setStateSearchTerm('');
    setShowStateDropdown(false);
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      setForm({ ...form, notes: [...form.notes, newNote.trim()] });
      setNewNote('');
    }
  };

  const handleRemoveNote = (index: number) => {
    setForm({ ...form, notes: form.notes.filter((_, i) => i !== index) });
  };

  const handleReorderNotes = (notes: string[]) => {
    setForm({ ...form, notes });
  };

  return {
    showForm,
    editing,
    form,
    searchTerm,
    newNote,
    stateSearchTerm,
    showStateDropdown,
    stateDropdownRef,
    showDeleteModal,
    deleteId,
    setShowForm,
    setEditing,
    setForm,
    setSearchTerm,
    setNewNote,
    setStateSearchTerm,
    setShowStateDropdown,
    handleCreate,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    handleSubmit,
    handleCancel,
    handleAddNote,
    handleRemoveNote,
    handleReorderNotes,
    isCreating,
    isUpdating,
    isDeleting,
  };
};

